import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Send, MessageSquare, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';

const API_URL = window.__WR_API_BASE__;

/**
 * Persistent team message bar — sits above the dashboard.
 * Shows latest message + input to send. Expandable to show recent messages.
 */
const TeamMessageBar = ({ user, teamUnreadCount, setTeamUnreadCount }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [sending, setSending] = useState(false);
  const inputRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/team/messages?limit=10`, { withCredentials: true });
      setMessages(res.data.messages || []);
      if (setTeamUnreadCount) setTeamUnreadCount(res.data.unread_count || 0);
    } catch { /* silent */ }
  }, [setTeamUnreadCount]);

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 45000);
    return () => clearInterval(interval);
  }, [fetchMessages]);

  const handleSend = async () => {
    if (!newMessage.trim() || sending) return;
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/team/messages`, {
        message: newMessage.trim(),
        message_type: 'chat'
      }, { withCredentials: true });
      setNewMessage('');
      fetchMessages();
    } catch {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleMarkRead = async () => {
    try {
      await axios.post(`${API_URL}/api/team/messages/mark-read`, {}, { withCredentials: true });
      fetchMessages();
    } catch { /* silent */ }
  };

  const latestMsg = messages.length > 0 ? messages[messages.length - 1] : null;

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
    if (diff < 60) return 'now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  };

  return (
    <div
      className="w-full border-b border-border/40 bg-card/80 backdrop-blur-sm"
      style={{ zIndex: 40 }}
      data-testid="team-message-bar"
    >
      {/* Compact bar — always visible */}
      <div className="flex items-center gap-2 px-3 sm:px-6 lg:px-8 py-1.5">
        {/* Latest message preview — left side */}
        {!expanded && latestMsg && (
          <div className="flex-1 min-w-0 flex items-center gap-1.5 overflow-hidden">
            <span className="text-[10px] font-medium text-primary shrink-0">{latestMsg.from_user_name}:</span>
            <span className="text-[10px] text-muted-foreground truncate">{latestMsg.message}</span>
            <span className="text-[9px] text-muted-foreground/50 shrink-0">{timeAgo(latestMsg.created_at)}</span>
          </div>
        )}
        {!expanded && !latestMsg && <div className="flex-1" />}
        {expanded && <div className="flex-1" />}

        {/* Inline send */}
        <div className="flex items-center gap-1 shrink-0">
          <Input
            ref={inputRef}
            placeholder="Message team..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
            onFocus={handleMarkRead}
            className="h-6 text-[11px] w-32 sm:w-48 bg-input/50 border-border/40"
            data-testid="team-bar-input"
          />
          <Button
            onClick={handleSend}
            disabled={!newMessage.trim() || sending}
            size="sm"
            variant="ghost"
            className="h-6 w-6 p-0"
            data-testid="team-bar-send"
          >
            <Send className="w-3 h-3" />
          </Button>
        </div>

        {/* Team toggle — far right */}
        <button
          onClick={() => { setExpanded(!expanded); if (!expanded) handleMarkRead(); }}
          className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors shrink-0 ml-2"
          data-testid="team-bar-toggle"
        >
          <MessageSquare className="w-3.5 h-3.5" />
          {teamUnreadCount > 0 && (
            <span className="bg-red-500 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
              {teamUnreadCount}
            </span>
          )}
          {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
      </div>

      {/* Expanded — recent messages */}
      {expanded && (
        <div className="px-3 sm:px-6 lg:px-8 pb-2 max-h-48 overflow-y-auto custom-scrollbar">
          {messages.length === 0 ? (
            <p className="text-[10px] text-muted-foreground text-center py-2">No messages yet</p>
          ) : (
            <div className="space-y-1">
              {messages.slice().reverse().slice(0, 8).map(msg => (
                <div key={msg.id} className="flex items-start gap-1.5 py-0.5">
                  <span className="text-[10px] font-medium text-primary shrink-0 w-16 truncate">{msg.from_user_name}</span>
                  <span className="text-[10px] text-foreground/80 flex-1">{msg.message}</span>
                  <span className="text-[9px] text-muted-foreground/40 shrink-0">{timeAgo(msg.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TeamMessageBar;
